import { Box, Grid } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import ProjectTable from './components/ProjectTable';
import ProjectViewPanel from './components/ProjectViewPanel';
import ProjectsToolbar from './components/ProjectsToolbar';
import CardModal from './createProjectModal/CardModal';
import { useProjectsList } from './useProjectsList';

export default function Projects() {
  const [viewPanelOpen, setViewPanelOpen] = React.useState(false);
  // When expanded, the panel takes the full content area (table hides).
  // Reset alongside close so re-opening a project always starts in
  // side-panel mode — the same convention the approvals ViewPanel uses.
  const [viewPanelExpanded, setViewPanelExpanded] = React.useState(false);
  const [selectedProject, setSelectedProject] = React.useState(null);
  const [selectedRowId, setSelectedRowId] = React.useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const lastCreatedProject = useSelector(
    (state) => state.projects.lastCreatedProject,
  );

  const {
    projects,
    totalCount,
    loading,
    isError,
    refetch,
    page,
    pageSize,
    search,
    debouncedSearch,
    showNoPermissions,
    handleSearchChange,
    handlePageChange,
    handlePageSizeChange,
  } = useProjectsList();

  // True while the table is between data sets: either the search input has
  // changed but the debounce hasn't fired, or RTK Query is mid-fetch. Used to
  // suppress the empty-state overlay so it doesn't flash a misleading message
  // (e.g. clearing a no-match search briefly shows "No projects to display"
  // before the new data arrives).
  const isSearchTransitioning = loading || search !== debouncedSearch;

  // Watch for new project creation
  React.useEffect(() => {
    if (lastCreatedProject) {
      // Refetch when a new project is created
      refetch();
    }
  }, [lastCreatedProject, refetch]);

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', p: 3 }}>
      <ProjectsToolbar
        search={search}
        onSearchChange={handleSearchChange}
      />

      {/* Table + panel layout. Both sides animate together via flex-basis
          so the panel slides in cleanly instead of mounting at full size
          while the table catches up. Panel is always mounted; its width
          collapses to 0 when no project is selected, and content renders
          conditionally on selectedProject so we don't pay for an inactive
          panel's render cost. */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', marginBottom: 2 }}>
        {/* Table hides entirely in expanded panel mode so the panel gets
            the full content area. `display: none` (not just width 0) so
            the DataGrid isn't asked to render into a 0-wide container,
            which would recompute row layouts unnecessarily. */}
        {!viewPanelExpanded && (
          <Box sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}>
            <ProjectTable
              projects={projects}
              totalCount={totalCount}
              loading={loading}
              isError={isError}
              page={page}
              pageSize={pageSize}
              showNoPermissions={showNoPermissions}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              selectedRowId={selectedRowId}
              deepLinkProjectId={searchParams.get('projectId')}
              onDeepLinkComplete={() => {
                searchParams.delete('projectId');
                setSearchParams(searchParams, { replace: true });
              }}
              onViewProject={(project) => {
                // Toggle behavior: clicking the already-open row's project
                // again closes the panel. Otherwise opens (or swaps) it.
                // Makes the row itself feel like a button instead of just
                // a passive "select" target.
                if (viewPanelOpen && selectedRowId === project.projectId) {
                  setViewPanelOpen(false);
                  setViewPanelExpanded(false);
                  setSelectedRowId(null);
                } else {
                  setSelectedProject(project);
                  setViewPanelOpen(true);
                  setSelectedRowId(project.projectId);
                }
              }}
              search={debouncedSearch}
              onClearSearch={() => handleSearchChange('')}
              isSearchTransitioning={isSearchTransitioning}
            />
          </Box>
        )}

        <Box sx={{
          // When expanded, the panel fills the row via flex: 1; otherwise
          // it holds the fixed 450px side-panel basis (or collapses to 0
          // when closed). Explicit flex-grow: 1 in expanded mode also
          // gives it any remaining space if a future row-mate is added.
          flex: viewPanelExpanded ? 1 : undefined,
          flexBasis: viewPanelExpanded
            ? 'auto'
            : viewPanelOpen
              ? 450
              : 0,
          flexShrink: 0,
          flexGrow: viewPanelExpanded ? 1 : 0,
          // Decelerating ease (cubic-bezier from Apple's standard timing
          // curve) — feels more natural than CSS 'ease' which has a
          // harder mid-transition slowdown. 300ms is the sweet spot for
          // panel slide-ins: long enough to track, short enough to not
          // feel sluggish.
          transition:
            'flex-basis 0.3s cubic-bezier(0.16, 1, 0.3, 1), margin-left 0.3s cubic-bezier(0.16, 1, 0.3, 1), border-left-width 0s linear 0.3s',
          marginLeft: viewPanelOpen && !viewPanelExpanded ? 2 : 0,
          borderLeft: viewPanelOpen && !viewPanelExpanded ? 1 : 0,
          borderColor: 'divider',
          overflow: 'hidden',
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Inner Box holds the content + handles the opacity fade so the
              panel contents don't pop into view mid-slide. We keep it
              mounted while viewPanelOpen so the closing transition can
              run; the actual ProjectViewPanel renders only when there's
              a selectedProject to avoid empty-state flicker. */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              opacity: viewPanelOpen ? 1 : 0,
              transition: 'opacity 0.25s ease 0.05s',
            }}
          >
            {selectedProject && (
              <ProjectViewPanel
                project={selectedProject}
                expanded={viewPanelExpanded}
                onToggleExpanded={() =>
                  setViewPanelExpanded((prev) => !prev)
                }
                onClose={() => {
                  setViewPanelOpen(false);
                  setViewPanelExpanded(false);
                  setSelectedRowId(null);
                }}
              />
            )}
          </Box>
        </Box>
      </Box>

      <CardModal />
    </Box>
  );
}
